import { StrictOmit } from '@enmon/commons';
import { TablePaginationConfig } from 'antd';
import { useAppMessages } from 'hooks/useAppMessages';
import { useIntl } from 'react-intl';
import { UseQueryOptions, UseQueryResult, useQuery } from 'react-query';
import { ReadingGroupListPagination } from 'routes/reading_groups/constants';
import { ReadingGroupsHooks as messages } from 'routes/reading_groups/messages';
import {
  FindInventoryMeterReadingGroupArgs,
  FindInventoryMeterReadingGroupResponse,
} from 'services/api/inventoryMeterReadingGroups';
import { useServices } from 'services/useServices';
import { QUERY_KEYS } from 'utils/queryKeys';

export interface UseInventoryMeterReadingGroupsArgs {
  filter?: FindInventoryMeterReadingGroupArgs['filter'];
  pagination?: StrictOmit<ReadingGroupListPagination, 'total'>;
  onSuccess?: (
    data: FindInventoryMeterReadingGroupResponse['data'],
    meta: { pagination?: TablePaginationConfig },
  ) => void;
}

/**
 * Hook to fetch inventory meter reading groups, optionally filtered by meter ID.
 */
export const useInventoryMeterReadingGroups = (
  { filter, pagination, onSuccess }: UseInventoryMeterReadingGroupsArgs = {},
  options: Pick<UseQueryOptions, 'enabled'> = {},
): UseQueryResult<FindInventoryMeterReadingGroupResponse> => {
  const intl = useIntl();
  const appMessages = useAppMessages();
  const {
    api: { inventoryMeterReadingGroupsApiClient },
  } = useServices();

  return useQuery({
    ...options,
    queryKey: QUERY_KEYS.inventoryMeterReadingGroups.filter({ ...filter, ...pagination }),
    queryFn: async ({ signal }) => inventoryMeterReadingGroupsApiClient.find({ filter, pagination, signal }),
    onError: () => appMessages.error(intl.formatMessage(messages.findGroupsError)),
    onSuccess: ({ data, meta }) => {
      onSuccess?.(data, meta ?? {});
    },
  });
};
